/* Update cart items */
if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.freeGiftId = this.dataset.freeGiftId;
      this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
      this.formElement = document.getElementById('cart_form');
      this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value),
        0
      );

      this.debouncedOnChange = debounce((event) => {
        if (
          event.target != document.getElementById('Cart-note') &&
          !(event.target instanceof HTMLSelectElement)
        ) {
          this.onChange(event);
        }
      }, 300);

      // See if we are on the cart page
      if (this.formElement) {
        this.formElement.addEventListener('submit', (e) => {
          const birthlistName = document.getElementById('CartName');
          const formRow = birthlistName.closest('.form-row');

          if (birthlistName && birthlistName.value == '') {
            formRow.classList.add('error');
            e.preventDefault();

            birthlistName.addEventListener('focus', (e) => {
              formRow.classList.remove('error');
            });
          }
        });
      }

      this.addEventListener('change', this.debouncedOnChange.bind(this));

      //add an custom event listener for giftAdded, and call onChange
      window.addEventListener('giftAdded', (event) => {
        this.refreshCart(event);
      });
    }

    onChange(event) {
      this.updateQuantity(
        event.target.dataset.key,
        event.target.value,
        document.activeElement.getAttribute('name'),
        event
      );
    }

    async refreshCart(event) {
      this.enableLoading();
      const body = JSON.stringify({
        id: event.detail.id + '',
        quantity: event.detail.quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body,
      });
      const parsedState = await response.json();
      // Loop sections
      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) ||
          document.getElementById(section.id);

        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });
      this.disableLoading();
    }

    getSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id,
          selector: '.js-cart-item-contents',
        },
        {
          id: 'main-cart-footer',
          section: document.getElementById('main-cart-footer').dataset.id,
          selector: '.js-cart-footer-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        },
      ];
    }

    /**
     * updateQuantity
     * @description Works together with the Ajax Cart API.
     * @see https://shopify.dev/docs/api/ajax/reference/cart
     * @param {Number} line
     * @param {Number} quantity
     * @param {String} name
     * @param {Object} event
     */
    async updateQuantity(key, quantity, name, event, ignoreError = false) {
      if (!key) {
        return;
      }
      this.enableLoading(key);

      const body = JSON.stringify({
        id: key,
        quantity: quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });
      //set the sanitized key to be everything after the colon
      const sanitized_key = key.split(':')[1];
      try {
        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body,
        });
        const parsedState = await response.json();

        this.parentElement.classList.toggle('cart-is-empty', parsedState.item_count === 0);

        // Error returned in request
        if (response.status === 422 && parsedState.errors) {
          // Read current updated quantity element
          const itemQuantityInput = document.getElementById(`Quantity-${sanitized_key}`);
          const currentValue = itemQuantityInput.value;
          const maxValue = itemQuantityInput.dataset.max;
          // Reset input value quantity
          if (currentValue > maxValue) itemQuantityInput.value = maxValue;
          // Show the error message to the users
          throw new Error(parsedState.errors);
        }

        // Tell the user that something is wrong when there are no sections returned from the call.
        if (!parsedState.sections) {
          event.target.value = event.target.defaultValue;
          throw new Error(window.cartStrings.error);
        }

        // Loop sections
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        this.updateLiveRegions(sanitized_key, parsedState.item_count);

        document
          .getElementById(`CartItem-${sanitized_key}`)
          ?.querySelector(`[name="${name}"]`)
          ?.focus();
        this.disableLoading();
      } catch (error) {
        document.querySelector('[data-cart-loader-active="show"]')?.classList.add('hidden');
        document.querySelector('[data-cart-loader-active="hidden"]')?.classList.remove('hidden');
        document.getElementById('cart-errors').textContent = error
          ? error
          : window.cartStrings.error;
        this.disableLoading();
      }
    }

    // show error texts, should not be deleted.
    updateLiveRegions(key, itemCount, ignoreError) {
      if (this.currentItemCount === itemCount && ignoreError == false) {
        document
          .getElementById(`Line-item-error-${key}`)
          .querySelector('.cart-item__error-text').innerHTML =
          window.cartStrings.quantityError.replace(
            '[quantity]',
            document.getElementById(`Quantity-${key}`).value
          );
      }

      this.currentItemCount = itemCount;
      this.lineItemStatusElement.setAttribute('aria-hidden', true);

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    enableLoading() {
      document.querySelector('cart-items').classList.add('cart-items-is-loading');
      document.querySelector('[data-cart-loader-active="show"]')?.classList.remove('hidden');
      document.querySelector('[data-cart-loader-active="hidden"]')?.classList.add('hidden');
      document.activeElement.blur();
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }

    disableLoading() {
      document.querySelector('cart-items').classList.remove('cart-items-is-loading');
    }
  }
  customElements.define('cart-items', CartItems);
}

/* Remove item from the cart  */
if (!customElements.get('cart-remove-button')) {
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('cart-items').updateQuantity(this.dataset.key, 0);
      });
    }
  }
  customElements.define('cart-remove-button', CartRemoveButton);
}

/* Update the cart note */
if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        'change',
        debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        }, 300)
      );
    }
  }
  customElements.define('cart-note', CartNote);
}

if (!customElements.get('cart-variant-select')) {
  class CartVariantSelect extends HTMLElement {
    constructor() {
      super();
      this.variantSelect = this.querySelector('select');
      this.variantSelect.addEventListener('change', this.onVariantChange.bind(this));
      this.key = this.getAttribute('data-key');
      this.previousQuantity = this.getAttribute('data-quantity');
      this.index = this.getAttribute('data-index');
    }

    onVariantChange() {
      this.newVariantId = this.variantSelect.options[this.variantSelect.selectedIndex].value;

      document.querySelector('cart-items').classList.add('cart-items-is-loading');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: this.newVariantId,
          quantity: this.previousQuantity,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          this.closest('cart-items').updateQuantity(this.key, 0, 'updates[]', null, true);
        });
    }
  }

  customElements.define('cart-variant-select', CartVariantSelect);
}
